import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';
import { uuid } from 'uuidv4';

import { ViewBillContentSection } from 'components/bill/ui/ViewBill/ViewBillContentSection';
import { blendColors, decompoundKey } from 'lib/helpers';
import { IBasketItem, IItemReadResourceV10 } from './model/Basket';
import { isDefined, isNumber } from 'lib/typeInference';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import {
    doesModifierTreeRequireSelection,
    ESuggestionType,
    getDefaultSelectedModifierProducts,
    getModifierOptionsForProduct,
    IEnrichedProduct,
    IOptionModifierSettings,
    IProductModifierSettings,
    isProductAvailable,
    mapProduct
} from 'components/menu/model/Menu';
import { BasketSuggestionItem } from './BasketSuggestionItem';
import { isUndefined } from 'lodash';
import { MenuProductInfo } from 'components/menu/MenuProductInfo';
import { useSuggestionsContext } from 'src/hooks/useSuggestionsContext';
import { addBasketItem, addBasketItems, setBasketPrepTime } from 'store/basket/basketActions';
import {
    MenuProductInfoTimePaddingDialog,
    PrepTimeModalInfo
} from 'components/menu/MenuProductInfoTimePaddingDialog';

import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { usePrepTimePadding } from 'src/hooks/usePrepTimePadding';

const MAX_SUGGESTIONS = 10;

export const getTotalDefaultModifiersPrice = (
    modifierProducts: IProductModifierSettings[],
    modifierOptions: IOptionModifierSettings[]
) => {
    let total = 0;
    const flattenedModifiers = [modifierProducts, modifierOptions].flat();
    for (const modifier of flattenedModifiers) {
        if ('options' in modifier && modifier.options && modifier.options.length > 0) {
            const selectedOptions = modifier.options.filter(option => option.selected);
            for (const selectedOption of selectedOptions) {
                total += selectedOption.price || 0;
            }
        } else if ('products' in modifier && modifier.products && modifier.products.length > 0) {
            const selectedProducts = modifier.products.filter(item => item.selected);
            for (const selectedProduct of selectedProducts) {
                total += selectedProduct.price || 0;
            }
        }
    }
    return total;
};

const Carousel = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    gap: theme.spacing(1),
    overflowX: 'auto',
    marginLeft: theme.spacing(-1.5),
    marginRight: theme.spacing(-1.5),
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5)
}));

interface IProps {
    loading?: boolean;
    items: IItemReadResourceV10[];
}

export const BasketSuggestions: React.FC<React.PropsWithChildren<IProps>> = ({ loading, items }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { logUserEvent } = useGAHelpers();
    const { checkPadding } = usePrepTimePadding();
    const context = useSuggestionsContext();

    const { menu } = useSelector((state: ApplicationState) => state.menu);
    const categories = useSelector((state: ApplicationState) => state.menu.categories);
    const currency = useSelector(
        (state: ApplicationState) => state.settings?.settings?.region?.currencyCode ?? 'GBP'
    );

    const [prepTimeModalInfo, setPrepTimeModalInfo] = React.useState<PrepTimeModalInfo | undefined>();
    const [selectedSuggestion, setSelectedSuggestion] = React.useState<IEnrichedProduct | null>(null);
    const [selectedSuggestionCategoryId, setSelectedSuggestionCategoryId] = React.useState<
        string | undefined
    >();

    const sectionBackgroundColor = blendColors(
        theme.palette.primary.main,
        theme.palette.background.default,
        0.9
    );

    const filteredBasketItems = React.useMemo(
        () => items.filter(item => !isDefined(item.parentReferenceId)),
        [items]
    );

    const suggestions = React.useMemo(() => {
        if (!menu) {
            return [];
        }
        const allSuggestions: IEnrichedProduct[] = [];
        const allSuggestionsIds = new Set<string>();
        const basketProductIds = new Set<string>(filteredBasketItems.map(item => item.productId));
        const basketProducts = filteredBasketItems
            .map(item => menu.productIdToProduct.get(item.productId))
            .filter(product => isDefined(product));
        const allSuggestionsFromBasket = basketProducts.flatMap(product => product?.suggestions ?? []);
        for (const suggestion of allSuggestionsFromBasket) {
            if (
                isUndefined(suggestion) ||
                suggestion.type !== ESuggestionType.ADD ||
                basketProductIds.has(String(suggestion?.productId))
            ) {
                continue;
            }
            const suggestionProduct = menu.productIdToProduct.get(String(suggestion?.productId));
            if (isUndefined(suggestionProduct) || !isProductAvailable(suggestionProduct)) {
                continue;
            }
            // Check for duplicates
            if (allSuggestionsIds.has(suggestionProduct.id)) {
                continue;
            }
            const mappedProduct = mapProduct(suggestionProduct, menu);
            allSuggestionsIds.add(mappedProduct.id);
            allSuggestions.push(mappedProduct);
            // Check if we have reached the maximum number of suggestions
            if (allSuggestions.length >= MAX_SUGGESTIONS) {
                break;
            }
        }

        return allSuggestions;
    }, [menu, filteredBasketItems]);

    const handleProductClick = React.useCallback(
        (product: IEnrichedProduct) => {
            logUserEvent(EGAEventName.BasketSuggestionTapped);
            const categoryId = menu?.productIdToCategoryId.get(product.id);
            if (product?.productGroupId) {
                const productGroup = categories
                    .find(item => item.id === categoryId)
                    ?.products.find(item => item.id === product.productGroupId);
                if (productGroup) {
                    const defaultSelectedProduct =
                        'products' in productGroup && Array.isArray(productGroup.products)
                            ? productGroup.products.find(item => item.id === product.id)
                            : null;
                    if (defaultSelectedProduct) {
                        context.setDefaultSuggestedValue(defaultSelectedProduct);
                    }
                    setSelectedSuggestion(productGroup);
                    setSelectedSuggestionCategoryId(categoryId);
                }
            } else {
                setSelectedSuggestion(product);
                setSelectedSuggestionCategoryId(categoryId);
            }
        },
        [menu, categories, logUserEvent, context]
    );

    const handleCloseSelectedSuggestion = React.useCallback(() => {
        setSelectedSuggestion(null);
        setSelectedSuggestionCategoryId(undefined);
        context.setDefaultSuggestedValue(null);
    }, [context]);

    const handleCancelProductInfo = React.useCallback(() => {
        logUserEvent(EGAEventName.BasketSuggestionDetailsAddCancelled);
        setSelectedSuggestion(null);
        setSelectedSuggestionCategoryId(undefined);
    }, [logUserEvent]);

    const handleCancelPrepTimeInfo = React.useCallback(() => {
        setPrepTimeModalInfo(undefined);
    }, []);

    const handleAddToBasket = React.useCallback(
        (product: IEnrichedProduct) => {
            if (!menu) {
                return;
            }
            logUserEvent(EGAEventName.BasketSuggestionAddTapped);

            if (product.modifiers && product.modifiers.length > 0) {
                const allOptionModifiers = getModifierOptionsForProduct(menu, product.id, product.modifiers);
                const requiredOptionModifiers = allOptionModifiers.filter(modifier => modifier.min > 0);

                // Check if there are mandatory option modifiers that requires selection
                if (requiredOptionModifiers.length > 0) {
                    for (const modifier of requiredOptionModifiers) {
                        if ('options' in modifier && modifier.options && modifier.options.length > 0) {
                            const selectedOptions = modifier.options.filter(option => option.selected);
                            if (selectedOptions.length < modifier.min) {
                                // show product info modal
                                setSelectedSuggestion(product);
                                setSelectedSuggestionCategoryId(menu?.productIdToCategoryId.get(product.id));
                                return;
                            }
                        }
                    }
                }

                const modifierProductsTreeRequiresSelection = doesModifierTreeRequireSelection(
                    menu,
                    product.modifiers
                );
                if (modifierProductsTreeRequiresSelection) {
                    setSelectedSuggestion(product);
                    setSelectedSuggestionCategoryId(menu?.productIdToCategoryId.get(product.id));
                    return;
                }

                // If there are no mandatory product or option modifiers, add item to basket
                // With default modifiers
                let timePadding = 0;
                const parentReferenceId = uuid();
                const defaultProductModifiers: (IBasketItem & { price: number })[] = [];

                const selectedModifierProducts = getDefaultSelectedModifierProducts(menu, product.modifiers);
                // Check time padding for default selected modifier products
                for (const selectedProduct of selectedModifierProducts) {
                    if (
                        isNumber(selectedProduct.prepTimeMins) &&
                        selectedProduct.prepTimeMins > timePadding
                    ) {
                        timePadding = selectedProduct.prepTimeMins;
                    }
                    const { parentId } = decompoundKey(selectedProduct.id);
                    defaultProductModifiers.push({
                        parentReferenceId,
                        quantity: 1,
                        categoryId: parentId ?? menu.productIdToCategoryId.get(selectedProduct.id) ?? '',
                        productId: selectedProduct.id,
                        price: 0
                    });
                }

                let price = product.price || 0;
                const optionModifiersPrice = allOptionModifiers
                    .flatMap(modifier => modifier.options)
                    .filter(option => option.selected)
                    .map(option => option.price || 0)
                    .reduce((acc, price) => acc + price, 0);
                const productModifiersPrice = selectedModifierProducts
                    .map(product => product.price || 0)
                    .reduce((acc, price) => acc + price, 0);
                price += optionModifiersPrice + productModifiersPrice;

                const categoryId = menu.productIdToCategoryId.get(product.id);
                const basketItem: IBasketItem & { price: number } = {
                    productId: product.id,
                    categoryId: categoryId || '',
                    quantity: 1,
                    modifiers: allOptionModifiers.map(modifier => ({
                        id: modifier.id,
                        options: modifier.options
                            .filter(option => option.selected)
                            .map(option => ({ id: option.id }))
                    })),
                    referenceId: parentReferenceId,
                    price: Math.round(price * 100) / 100
                };

                if (isNumber(product.prepTimeMins) && product.prepTimeMins > timePadding) {
                    timePadding = product.prepTimeMins;
                }
                const isPaddingBigger = checkPadding(timePadding);
                const addItemToBasketFunction = () => {
                    const productsToAdd = [basketItem, ...defaultProductModifiers];
                    if (isPaddingBigger) {
                        setBasketPrepTime(timePadding)(dispatch);
                    }
                    addBasketItems(productsToAdd)(dispatch);
                    // Clear prep time modal info
                    setPrepTimeModalInfo(undefined);
                };
                if (isPaddingBigger) {
                    // Show prep time modal
                    setPrepTimeModalInfo({
                        onSuccess: addItemToBasketFunction,
                        onCancel: handleCancelPrepTimeInfo,
                        productTitle: product.title,
                        timePadding
                    });
                } else {
                    addItemToBasketFunction();
                }
            } else {
                const categoryId = menu.productIdToCategoryId.get(product.id);
                addBasketItem(product, categoryId || '', 1, undefined, product.price)(dispatch);
            }
        },
        [dispatch, menu, checkPadding, handleCancelPrepTimeInfo, logUserEvent]
    );

    const handleCarouselScroll = React.useCallback(() => {
        logUserEvent(EGAEventName.BasketSuggestionsSwiped);
    }, [logUserEvent]);

    if (loading || !suggestions.length) {
        return null;
    }

    return (
        <ViewBillContentSection
            title={t('CHECKOUT_SUGGESTIONS_TITLE')}
            bottomSpacing={2}
            backgroundColor={sectionBackgroundColor}
        >
            <Carousel className="hidden-scroll" onScroll={handleCarouselScroll}>
                {suggestions.map(suggestion => (
                    <BasketSuggestionItem
                        key={suggestion?.id}
                        product={suggestion}
                        onAddToBasket={handleAddToBasket}
                        onProductClick={handleProductClick}
                    />
                ))}
            </Carousel>
            {selectedSuggestion && isDefined(selectedSuggestionCategoryId) && (
                <MenuProductInfo
                    item={selectedSuggestion}
                    currency={currency}
                    open={!!selectedSuggestion}
                    onClose={handleCloseSelectedSuggestion}
                    onCancel={handleCancelProductInfo}
                    categoryId={selectedSuggestionCategoryId}
                />
            )}
            <MenuProductInfoTimePaddingDialog
                open={!!prepTimeModalInfo}
                info={prepTimeModalInfo}
                onCancel={handleCancelPrepTimeInfo}
            />
        </ViewBillContentSection>
    );
};
